import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";

export const CATEGORIES = [
  { value: "faq", label: "FAQ" },
  { value: "policy", label: "Policy" },
  { value: "services", label: "Services" },
  { value: "locations", label: "Locations" },
  { value: "payments", label: "Payments" },
  { value: "onboarding", label: "Onboarding" },
  { value: "accounts", label: "Accounts" },
  { value: "support_procedures", label: "Support Procedures" },
];

const KnowledgeArticleForm = ({
  initialValues,
  onSubmit,
  isLoading = false,
  isEdit = false,
}) => {
  const history = useHistory();

  const [articleData, setArticleData] = useState({
    title: "",
    category: "faq",
    content: "",
    keywords: [],
    status: "published",
  });

  const [keywordInput, setKeywordInput] = useState("");

  useEffect(() => {
    if (initialValues) {
      setArticleData({
        title: initialValues.title || "",
        category: initialValues.category || "faq",
        content: initialValues.content || "",
        keywords: Array.isArray(initialValues.keywords)
          ? initialValues.keywords
          : typeof initialValues.keywords === "string"
          ? initialValues.keywords.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        status: initialValues.status || "published",
      });
    }
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setArticleData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (!trimmed) return;

    // Support comma-separated batch adding
    const tagsToAdd = trimmed
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t && !articleData.keywords.includes(t));

    if (tagsToAdd.length > 0) {
      setArticleData((prev) => ({
        ...prev,
        keywords: [...prev.keywords, ...tagsToAdd],
      }));
    }
    setKeywordInput("");
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddKeyword();
    } else if (
      e.key === "Backspace" &&
      !keywordInput &&
      articleData.keywords.length > 0
    ) {
      handleRemoveKeyword(articleData.keywords.length - 1);
    }
  };

  const handleRemoveKeyword = (indexToRemove) => {
    setArticleData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    if (!articleData.title.trim()) {
      toast.error("Please enter Article Title");
      return;
    }
    if (!articleData.category) {
      toast.error("Please select a Category");
      return;
    }
    if (!articleData.content.trim()) {
      toast.error("Please enter Article Content");
      return;
    }

    // Auto-commit any keyword still in the input field
    let finalKeywords = [...articleData.keywords];
    if (keywordInput.trim()) {
      const extra = keywordInput
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t && !finalKeywords.includes(t));
      finalKeywords = [...finalKeywords, ...extra];
    }

    const payload = {
      title: articleData.title.trim(),
      category: articleData.category,
      content: articleData.content.trim(),
      keywords: finalKeywords,
      status: articleData.status,
    };

    onSubmit(payload);
  };

  return (
    <Card className="shadow">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">
            {isEdit ? "Edit Knowledge Article" : "Add Knowledge Article"}
          </h3>
          <Button
            color="secondary"
            size="sm"
            onClick={() => history.push("/admin/chatbot-knowledge")}
          >
            <i className="fas fa-arrow-left mr-1"></i> Back
          </Button>
        </div>

        <Row className="d-flex">
          {/* Article Title - Full Width */}
          <Col md="12" className="my-2">
            <Label className="mb-0 my-auto">
              Article Title <span className="text-danger">*</span>
            </Label>
            <Input
              name="title"
              placeholder="e.g. When do service providers get paid?"
              value={articleData.title}
              required
              onChange={handleChange}
            />
          </Col>

          {/* Category Dropdown & Status Dropdown in One Line */}
          <Col md="6" className="my-2">
            <Label className="mb-0 my-auto">
              Category <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="category"
              value={articleData.category}
              onChange={handleChange}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </Input>
          </Col>

          <Col md="6" className="my-2">
            <Label className="mb-0 my-auto">
              Status <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="status"
              value={articleData.status}
              onChange={handleChange}
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </Input>
          </Col>

          {/* Keywords - Modern Tag Input */}
          <Col md="12" className="my-2">
            <Label className="mb-0 my-auto">
              Keywords (Tags)
            </Label>
            <div
              className="form-control d-flex flex-wrap align-items-center mt-1"
              style={{
                minHeight: "46px",
                height: "auto",
                padding: "4px 8px",
                cursor: "text",
                backgroundColor: "#fff",
              }}
              onClick={() => {
                const el = document.getElementById("article-keyword-input");
                if (el) el.focus();
              }}
            >
              {articleData.keywords && articleData.keywords.length > 0 &&
                articleData.keywords.map((kw, idx) => (
                  <span
                    key={idx}
                    className="badge badge-pill mr-2 my-1 px-3 py-2 d-inline-flex align-items-center"
                    style={{
                      backgroundColor: "#e8f5f4",
                      color: "#008080",
                      fontSize: "13px",
                      fontWeight: "500",
                      border: "1px solid #b2dfdb",
                    }}
                  >
                    {kw}
                    <i
                      className="fas fa-times ml-2 text-danger"
                      style={{ cursor: "pointer", fontSize: "11px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveKeyword(idx);
                      }}
                      title="Remove tag"
                    />
                  </span>
                ))}
              <input
                id="article-keyword-input"
                type="text"
                placeholder={
                  articleData.keywords.length === 0
                    ? "Type keyword and press Enter or comma..."
                    : "Add more..."
                }
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                onBlur={handleAddKeyword}
                style={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  flex: "1 1 180px",
                  fontSize: "14px",
                  background: "transparent",
                  padding: "4px 6px",
                  minWidth: "160px",
                }}
              />
            </div>
            <small className="text-muted">
              Type keyword and press Enter or comma to add.
            </small>
          </Col>

          {/* Article Content */}
          <Col md="12" className="my-2">
            <Label className="mb-0 my-auto">
              Article Content <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              name="content"
              rows="8"
              placeholder="Payment is released to the provider 3 business days after the buyer approves the work..."
              value={articleData.content}
              required
              onChange={handleChange}
              style={{ fontSize: "14px", lineHeight: "1.6" }}
            />
          </Col>
        </Row>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end align-items-center mt-4">
          <div>
            <Button
              color="secondary"
              className="mx-2"
              onClick={() => history.push("/admin/chatbot-knowledge")}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className="mx-2"
              disabled={isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? <Spinner size="sm" /> : "Save"}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default KnowledgeArticleForm;
